import { useEffect, useState } from "react";
import { useNavigate, useLocation, Link, Navigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { GraduationCap, ArrowRight } from "lucide-react";
import KinglyLogo from "@/components/KinglyLogo";
import { MfaStep } from "@/components/auth/MfaStep";
import { DeviceApprovalWait } from "@/components/auth/DeviceApprovalWait";
import { toast } from "sonner";
import { useLang } from "@/i18n";

export default function Login() {
  const { lang, setLang } = useLang();
  const { login, verifyMfa, resendMfa, pollApproval, approvalUseCode, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [challenge, setChallenge] = useState(null);
  const [approval, setApproval] = useState(null);

  const from = location.state?.from;
  const dest = from?.pathname && from.pathname !== "/login" ? `${from.pathname}${from.search || ""}` : "/dashboard";
  useEffect(() => { if (user?.demo) logout(); }, [user?.demo]); // eslint-disable-line react-hooks/exhaustive-deps -- leaving the demo barn to sign in
  if (user && !user.demo) return <Navigate to={dest} replace />;

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    const r = await login(email, password);
    setBusy(false);
    if (r.ok) {
      toast.success("Welcome back");
      navigate(dest);
    } else if (r.mfa) {
      setChallenge(r.mfa);
    } else if (r.approval) {
      setApproval(r.approval);
    } else {
      toast.error(r.error);
    }
  }

  async function onApprovalPoll(id, token, trust) {
    const r = await pollApproval(id, token, trust);
    if (r.ok) { toast.success("Approved — welcome back"); navigate(dest); }
    return r;
  }

  async function onApprovalCode(id, token) {
    const r = await approvalUseCode(id, token);
    if (r.ok) { setApproval(null); setChallenge(r.mfa); toast.success("Code sent"); }
    return r;
  }

  async function onVerify(code, trust) {
    const r = await verifyMfa(challenge.challenge_id, code, trust);
    if (r.ok) { toast.success("Welcome back"); navigate(dest); return r; }
    if (r.status === 410) { setChallenge(null); toast.error(r.error); }
    return r;
  }

  async function onResend(channel) {
    const r = await resendMfa(challenge.challenge_id, channel);
    if (r.ok) { setChallenge(r.challenge); toast.success(channel === "email" ? "Code emailed" : "Code sent again"); }
    else if (r.status === 410) { setChallenge(null); toast.error(r.error); }
    return r;
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white">
      <div className="flex items-center justify-center p-8">
        {challenge || approval ? (
          <div className="w-full max-w-sm">
            <div className="flex items-center gap-3 mb-8">
              <KinglyLogo size={56} />
              <div>
                <div className="font-display font-black text-slate-900 leading-none text-2xl tracking-tight">Kingly</div>
                <div className="text-[11px] text-amber-700 uppercase tracking-[0.22em] font-semibold mt-1.5">Be the King of the Show</div>
              </div>
            </div>
            {challenge ? (
              <MfaStep challenge={challenge} onVerify={onVerify} onResend={onResend} onBack={() => setChallenge(null)} theme="light" testPrefix="mfa" />
            ) : (
              <DeviceApprovalWait approval={approval} onPoll={onApprovalPoll} onUseCode={onApprovalCode} onBack={() => setApproval(null)} />
            )}
          </div>
        ) : (
        <form onSubmit={submit} className="w-full max-w-sm space-y-6" data-testid="login-form">
          <div>
            <div className="flex items-center gap-3 mb-8">
              <KinglyLogo size={72} />
              <div>
                <div className="font-display font-black text-slate-900 leading-none text-3xl tracking-tight">Kingly</div>
                <div className="text-[11px] text-amber-700 uppercase tracking-[0.22em] font-semibold mt-1.5">Be the King of the Show</div>
              </div>
            </div>
            <h1 className="text-2xl md:text-3xl font-display font-bold text-slate-900">Welcome back</h1>
            <p className="text-sm text-slate-500 mt-2">Sign in to manage shows, horses & invoices.</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">
                Email
              </Label>
              <Input
                id="email"
                data-testid="login-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                required
                className="mt-1"
              />
            </div>
            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">
                  Password
                </Label>
                <Link
                  to="/forgot-password"
                  data-testid="forgot-password-link"
                  className="text-xs text-amber-700 hover:text-amber-900 font-medium"
                >
                  Forgot password?
                </Link>
              </div>
              <Input
                id="password"
                data-testid="login-password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="mt-1"
              />
            </div>
          </div>
          <Button data-testid="login-submit" type="submit" disabled={busy} className="w-full bg-slate-900 hover:bg-slate-800">
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <p className="text-sm text-slate-500 text-center">
            No account?{" "}
            <Link to="/register" className="text-blue-600 font-medium hover:underline" data-testid="link-to-register">
              Create one
            </Link>
          </p>
          <button type="button" onClick={() => setLang(lang === "es" ? "en" : "es")} data-no-translate data-testid="login-lang-toggle"
            className="w-full text-center text-xs text-slate-500 hover:text-slate-800 py-1">
            {lang === "es" ? "Switch to English" : "Ver en español"}
          </button>
          <Link to="/collegiate" data-testid="link-to-collegiate"
                className="flex items-center justify-between rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-slate-800 hover:bg-amber-100 transition-colors">
            <span className="flex items-center gap-2.5">
              <GraduationCap className="w-4 h-4 text-amber-700" />
              <span><span className="font-semibold">Collegiate team?</span> Coach & athlete login</span>
            </span>
            <ArrowRight className="w-4 h-4 text-amber-700" />
          </Link>
          <p className="text-xs text-slate-400 text-center">
            Demo · admin@stable.app / admin123
          </p>
          <p className="text-xs text-slate-400 text-center -mt-3">
            By signing in, you agree to our{" "}
            <Link to="/terms" className="underline hover:text-slate-600" data-testid="terms-link-login">
              Terms
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="underline hover:text-slate-600" data-testid="privacy-link-login">
              Privacy Policy
            </Link>
            .
          </p>
        </form>
        )}
      </div>
      <div
        className="hidden md:block relative bg-cover bg-center"
        style={{
          backgroundImage:
            "linear-gradient(135deg, rgba(15,23,42,0.55), rgba(15,23,42,0.25)), url('https://customer-assets.emergentagent.com/job_horse-show-manager/artifacts/73qbm1gd_Carly.jpg')",
          backgroundPosition: "center 35%",
        }}
      >
        <div className="absolute bottom-10 left-10 right-10 text-white">
          <div className="text-xs uppercase tracking-[0.3em] mb-3 text-amber-300 font-semibold">Be the King of the Show</div>
          <h2 className="text-3xl font-display font-semibold leading-tight">
            Rule every show ring with confidence — horses, day fees & invoices, all in one place.
          </h2>
          <p className="text-[11px] text-white/70 mt-4 tracking-wide">
            Carly Veldman Parks · Parks Show Horses · AQHA World Show
          </p>
        </div>
      </div>
    </div>
  );
}
